import { eq, useLiveQuery } from '@tanstack/react-db';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { ProductForm } from '@/components/catalog/product-form';
import { useDetailTitle } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  brandsCollection,
  categoriesCollection,
  type ProductRecord,
  type ProductTagAssignmentRecord,
  productsCollection,
  productTagAssignmentsCollection,
  productTagsCollection,
  strainsCollection,
  type VariantRecord,
  variantsCollection,
} from '@/lib/collections';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/products/$productId')({
  loader: async () => {
    await Promise.all([
      productsCollection.preload(),
      variantsCollection.preload(),
      brandsCollection.preload(),
      categoriesCollection.preload(),
      strainsCollection.preload(),
      productTagsCollection.preload(),
      productTagAssignmentsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: ProductDetailPage,
});

function ProductDetailPage() {
  const { productId } = useParams({ from: '/products/$productId' });

  const { data, isLoading } = useLiveQuery(
    (q) =>
      q.from({ product: productsCollection }).where(({ product }) => eq(product.id, productId)),
    [productId],
  );

  const product = (data ?? [])[0] as ProductRecord | undefined;

  /*
   * The two columns the list deliberately does not carry.
   *
   * `products` syncs an explicit column list, without the description --
   * across the catalog it is almost the entire payload. The editor is the
   * one place that needs it, so it fetches them for the single row it is
   * editing.
   */
  const detail = useQuery({
    queryKey: ['product-detail', productId],
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('products')
        .select('description, lab_results_url')
        .eq('id', productId)
        .maybeSingle();

      if (error) throw error;

      return row ?? { description: null, lab_results_url: null };
    },
  });

  const variants = useLiveQuery(
    (q) =>
      q
        .from({ variant: variantsCollection })
        .where(({ variant }) => eq(variant.product_id, productId)),
    [productId],
  );

  const assignments = useLiveQuery(
    (q) =>
      q
        .from({ assignment: productTagAssignmentsCollection })
        .where(({ assignment }) => eq(assignment.product_id, productId)),
    [productId],
  );

  /*
   * Publishes the third breadcrumb: [collapse | Products | this record]. The
   * name rather than the id, and it updates live while the name is edited,
   * because the crumb is how you know which record you are looking at.
   */
  useDetailTitle(product?.name ?? 'Product');

  /*
   * The description arrives in its own request, and the editor is
   * uncontrolled once mounted -- mounting the form before it lands would
   * show an empty body for a product that has one, and saving would then
   * erase it.
   */
  if (isLoading || detail.isLoading) {
    return (
      <div className="flex max-w-4xl flex-col gap-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-[24rem] flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12 text-center">
        <h2 className="text-lg font-medium">Product not found</h2>
        <p className="text-muted-foreground max-w-md text-sm">
          It may have been deleted, or it belongs to another workspace.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/products">Back to products</Link>
        </Button>
      </div>
    );
  }

  return (
    <ProductForm
      product={{ ...product, ...(detail.data ?? {}) }}
      variants={(variants.data ?? []) as VariantRecord[]}
      assignments={(assignments.data ?? []) as ProductTagAssignmentRecord[]}
    />
  );
}
